import { medianOf } from "./compareDiscrepancies";

const BASELINE_PREF = [
    "anthropic/claude-haiku-4.5",
    "google/gemini-2.5-flash",
    "google/gemini-3.5-flash",
];

export function isCriticalFailureReason(reason) {
    const t = String(reason || "").toLowerCase();
    return (
        t.includes("trunc") ||
        t.includes("vazio") ||
        t.includes("json") ||
        t.includes("schema") ||
        t.includes("invent") ||
        t.includes("em vez de escalar") ||
        t.includes("respondeu em vez")
    );
}

export function resolveBaselineModelId(models, preferred) {
    const ids = (models || []).map((m) => m.id);
    for (const id of preferred || BASELINE_PREF) {
        if (ids.includes(id)) return id;
    }
    return ids[0] || null;
}

export function modelShortLabel(models, slot) {
    const entry = (Array.isArray(models) ? models : []).find((m) => m.slot === slot);
    const id = entry?.id || "";
    const tail = id.split("/").pop();
    return tail || id || `slot${slot}`;
}

function sumCost(rows) {
    let total = 0;
    let any = false;
    for (const r of rows) {
        const c = r?.usage?.cost_usd ?? r?.cost_usd;
        if (Number.isFinite(Number(c))) {
            total += Number(c);
            any = true;
        }
    }
    return any ? total : null;
}

export function computeCompareScorecard({
    results,
    models,
    suiteSnapshot,
    baselineModelId,
} = {}) {
    const list = Array.isArray(models) ? models : [];
    const baseline =
        baselineModelId || resolveBaselineModelId(list, BASELINE_PREF);
    const turnsByCase = new Map();
    for (const c of suiteSnapshot?.cases || []) {
        for (const t of c.turns || []) {
            turnsByCase.set(String(t.id), t.description || t.mensagem || t.id);
        }
    }

    const perModel = list.map((m) => {
        const rows = (results || []).filter((r) => r.slot === m.slot);
        const failures = [];
        let pass = 0;
        for (const r of rows) {
            if (r.pass === true) pass += 1;
            if (r.pass === false) {
                const assertId = Array.isArray(r.assertIds) && r.assertIds[0]
                    ? r.assertIds[0]
                    : "assert";
                failures.push({
                    turnId: String(r.turnId),
                    caseLabel: turnsByCase.get(String(r.turnId)) || String(r.turnId),
                    assertId,
                    reason: r.reason || "assert falhou",
                });
            }
        }
        return {
            id: m.id,
            pass,
            total: rows.length,
            failures,
            latencyMsMedian: medianOf(rows.map((r) => r.latencyMs)),
            costUsdTotal: sumCost(rows),
        };
    });

    const base = perModel.find((p) => p.id === baseline);
    const candidates = perModel.filter((p) => p.id !== baseline);
    let verdict = "empate_com_baseline";
    let verdictSummary = "Sem candidato além do baseline.";

    if (candidates.length === 0) {
        return { baselineModelId: baseline, perModel, verdict, verdictSummary };
    }

    const ranked = [...candidates].sort((a, b) => {
        const pa = a.total ? a.pass / a.total : 0;
        const pb = b.total ? b.pass / b.total : 0;
        if (pb !== pa) return pb - pa;
        return (a.costUsdTotal ?? 1e9) - (b.costUsdTotal ?? 1e9);
    });
    const best = ranked[0];
    const baseRatio = base && base.total ? base.pass / base.total : 0;
    const bestRatio = best.total ? best.pass / best.total : 0;
    const critical = best.failures.some((f) => isCriticalFailureReason(f.reason));

    if (critical || bestRatio < baseRatio) {
        verdict = "reprovado";
        verdictSummary = `${modelShortLabel(list, list.find((x) => x.id === best.id)?.slot)} ficou ${best.pass}/${best.total} (baseline ${base ? `${base.pass}/${base.total}` : "—"})${critical ? " com falha crítica" : ""} — reprovado pelo scorecard.`;
    } else if (bestRatio === baseRatio) {
        verdict = "empate_com_baseline";
        verdictSummary = `${modelShortLabel(list, list.find((x) => x.id === best.id)?.slot)} empatou asserts com o baseline; custo/latência informativos.`;
    } else {
        const bestCost = best.costUsdTotal;
        const baseCost = base?.costUsdTotal;
        const bothCostsKnown =
            Number.isFinite(bestCost) && Number.isFinite(baseCost);
        const bestLabel = modelShortLabel(
            list,
            list.find((x) => x.id === best.id)?.slot
        );

        if (!bothCostsKnown || bestCost < baseCost) {
            verdict = "apto_para_promocao";
            verdictSummary = `${bestLabel} superou o baseline em asserts sem falha crítica — apto para promoção (revisão manual).`;
        } else {
            verdict = "empate_com_baseline";
            verdictSummary = `${bestLabel} superou asserts do baseline, mas custo não é menor — empate (qualidade melhor, custo não menor).`;
        }
    }

    return { baselineModelId: baseline, perModel, verdict, verdictSummary };
}
